import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export default function VerifyCode() {
  const { t } = useTranslation();
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [timer, setTimer] = useState(600);
  const [loading, setLoading] = useState(false);

  const inputRefs = useRef([]);

  if (inputRefs.current.length === 0) {
    inputRefs.current = Array(6)
      .fill(null)
      .map(() => React.createRef());
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setEmail(params.get("email"));

    inputRefs.current[0].current?.focus();

    const interval = setInterval(() => {
      setTimer((t) => (t > 0 ? t - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  function handleChange(value, index) {
    if (!/^[0-9]?$/.test(value)) return;

    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);

    if (value && index < 5) {
      inputRefs.current[index + 1].current?.focus();
    }
  }

  function handleBackspace(e, index) {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1].current?.focus();
    }
  }

  async function handleVerify(e) {
    e.preventDefault();

    if (loading) return;

    const code = digits.join("");

    if (code.length !== 6) {
      setError(t("verifyCode.errors.incomplete"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await fetch("/api/membre?action=verifyCode", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          code,
        }),
      });

      const data = await res.json();

      if (!data.success) {
        setError(data.message);
        return;
      }

      sessionStorage.setItem("resetEmail", email);
      sessionStorage.setItem("resetCode", code);

      window.location.href = "/membre/reset";
    } catch (err) {
      console.error("VERIFY CODE ERROR:", err);
      setError(t("verifyCode.errors.network"));
    } finally {
      setLoading(false);
    }
  }

  const minutes = String(Math.floor(timer / 60)).padStart(2, "0");
  const seconds = String(timer % 60).padStart(2, "0");

  async function resend() {
    if (timer > 0) return;

    await fetch("/api/membre/requestReset", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });

    setTimer(600);
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-gradient-to-br from-blue-600 to-indigo-800">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white shadow-2xl rounded-3xl p-8 w-full max-w-sm"
      >
        <h1 className="text-2xl font-extrabold text-center text-gray-800 mb-3">
          {t("verifyCode.title")}
        </h1>

        <p className="text-gray-500 text-center text-sm mb-5">
          {t("verifyCode.sentTo")} <b>{email}</b>
        </p>

        <form onSubmit={handleVerify} className="space-y-6" method="POST">
          <div className="flex justify-center gap-3 w-full">
            {digits.map((digit, i) => (
              <input
                key={i}
                ref={inputRefs.current[i]}
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(e.target.value, i)}
                onKeyDown={(e) => handleBackspace(e, i)}
                className="w-12 h-12 text-center text-xl font-bold rounded-xl border bg-gray-50 
                  focus:ring-2 focus:ring-blue-600 outline-none"
              />
            ))}
          </div>

          <motion.button
            type="submit"
            whileTap={{ scale: 0.97 }}
            disabled={loading}
            className={`
    w-full py-3 rounded-xl text-white font-semibold shadow-lg
    transition flex items-center justify-center
    ${
      loading
        ? "bg-blue-300 cursor-not-allowed"
        : "bg-blue-600 hover:bg-blue-700"
    }
  `}
          >
           {loading ? t("verifyCode.verifying") : t("verifyCode.verify")}
          </motion.button>
        </form>

        <p className="text-center mt-4 text-gray-600 text-sm">
           {t("verifyCode.expiresIn")}{" "}
          <b>
            {minutes}:{seconds}
          </b>
        </p>

        <button
          onClick={resend}
          disabled={timer > 0}
          className={`mt-3 w-full text-sm py-2 rounded-xl transition ${
            timer > 0 ? "text-gray-400" : "text-blue-600 hover:underline"
          }`}
        >
          {t("verifyCode.resend")}
        </button>

        {error && <p className="text-red-500 text-center mt-3">{error}</p>}

        <button
          onClick={() => (window.location.href = "/membre/login")}
          className="w-full mt-6 text-sm text-gray-600 underline hover:text-gray-800"
        >
         {t("verifyCode.backLogin")}
        </button>
      </motion.div>
    </div>
  );
}
